/**
 * Splits a continuous 16 kHz microphone stream into utterances, so the microphone can stay on
 * while the child practises. Energy-based with an adaptive noise floor:
 *
 * - 20 ms frames; the noise floor follows quiet frames quickly downwards and slowly upwards
 * - speech starts after `onsetFrames` frames above floor + `onsetDb`, keeping `preRollMs` before it
 * - speech ends after `hangoverMs` below floor + `offsetDb`, or more than `peakDropDb` below the
 *   utterance's own peak (so short pauses inside a word or between syllables, as in "rab-bit",
 *   do not split it, while quieter background talk, e.g. a TV, does not prolong it)
 * - utterances shorter than `minSpeechMs` are dropped (coughs, clicks); longer than `maxSpeechMs`
 *   are cut and emitted with `cut` set (chatter or a sentence rather than one word)
 *
 * Options can change while running (`configure`), e.g. a stricter onset while the app is talking
 * (barge-in) or a longer hangover while the child practises prolonged sounds.
 */

export interface EndpointerOptions {
  sampleRate: number;
  onsetDb: number;
  offsetDb: number;
  onsetFrames: number;
  preRollMs: number;
  hangoverMs: number;
  minSpeechMs: number;
  maxSpeechMs: number;
  /**
   * Inside an utterance, frames this far below its loudest frame no longer count as speech. The
   * child is close to the device; a television or other people in the room are typically 15 dB
   * or more quieter (QA simulated users, TV room).
   */
  peakDropDb: number;
  /** Floor for the noise estimate in dBFS, so digital silence does not make everything "speech". */
  minNoiseDb: number;
  /**
   * Echo guard, for while the app talks and its own voice may leak into the microphone. The echo
   * level (90th percentile of what the microphone heard during the last 5 s of the app talking,
   * kept across turns) is learned; speech must be `ECHO_MARGIN_DB` louder than it to count as a
   * barge-in, and there is no barge-in until about a second of the app's speech has been heard.
   */
  echoGuard: boolean;
}

/** After an utterance ends, a new one needs to come near its loudness for this long. */
const REFRACTORY_MS = 700;

export const DEFAULT_ENDPOINTER: EndpointerOptions = {
  sampleRate: 16000,
  onsetDb: 12,
  offsetDb: 7,
  onsetFrames: 3,
  preRollMs: 400,
  hangoverMs: 450,
  // A crisp "rah" can have only 200-250 ms of voice within 18 dB of its peak (iPhone QA
  // 2026-10-11: one was dropped as too short); taps and clicks are shorter than this.
  minSpeechMs: 150,
  maxSpeechMs: 3500,
  peakDropDb: 18,
  minNoiseDb: -65,
  echoGuard: false,
};

export type EndpointerEvent =
  | {
      type: 'speech-start';
      /** Start of the audio kept (pre-roll included). */
      atMs: number;
      /** When the voice itself began: the first frame of the loud run that started it. */
      voiceAtMs: number;
    }
  | {
      type: 'utterance';
      audio: Float32Array;
      startMs: number;
      endMs: number;
      /** Reached `maxSpeechMs` and was cut. */
      cut: boolean;
      /** Loudest 20 ms frame and the noise floor, in dBFS. */
      peakDb: number;
      noiseDb: number;
      /** Share of samples at or near full scale (shouting into the microphone clips). */
      clippedShare: number;
      /** Voiced time after the latest `mark()` (all voiced time if marked before the start). */
      voicedMsSinceMark: number;
    }
  | { type: 'discarded'; reason: 'too-short'; peakDb: number };

const FRAME_MS = 20;
/** Frames at the start used only to measure the noise floor (no speech detection). */
const CALIBRATION_FRAMES = 20;
/** During calibration, a frame this far above the onset threshold is a voice, not the room. */
const CALIBRATION_VOICE_DB = 6;
/** Window for the minimum-statistics floor that adapts even during long "speech". */
const MIN_WINDOW_FRAMES = 100;
const CLIP_LEVEL = 0.98;
const ECHO_CALIBRATION_FRAMES = 50;
const ECHO_HISTORY_FRAMES = 250;
const ECHO_PERCENTILE = 0.9;
const ECHO_MARGIN_DB = 6;

export class Endpointer {
  private readonly frameSize: number;
  private options: EndpointerOptions;
  private pending = new Float32Array(0);
  private noiseDb: number;
  private framesSeen = 0;
  private calibrated = false;
  private loudRun = 0;
  private quietRun = 0;
  /** Frames since the last one above the noise floor (for trimming; quietRun ends speech). */
  private silentRun = 0;
  /** After an utterance: its peak and when it ended, for the refractory onset. */
  private lastPeakDb = -Infinity;
  private lastEndFrame = -Infinity;
  private inSpeech = false;
  private preRoll: Float32Array[] = [];
  private speech: Float32Array[] = [];
  private speechStartFrame = 0;
  /** Frames above the speech threshold in the current utterance. */
  private voicedFrames = 0;
  private peakDb = -Infinity;
  private lastDb = -Infinity;
  private clipped = 0;
  private samples = 0;
  private recentDb: number[] = [];
  private voicedSinceMark = 0;
  /** Levels heard while the app talked (recent history), their percentile, and a floor for it. */
  private echoHistory: number[] = [];
  private echoDb = -Infinity;
  private echoMinDb = -Infinity;
  private echoFrames = 0;

  constructor(options: Partial<EndpointerOptions> = {}) {
    this.options = { ...DEFAULT_ENDPOINTER, ...options };
    this.frameSize = Math.round((this.options.sampleRate * FRAME_MS) / 1000);
    this.noiseDb = this.options.minNoiseDb;
  }

  /** Changes options from the next frame on (the sample rate is fixed). */
  configure(options: Partial<Omit<EndpointerOptions, 'sampleRate'>>): void {
    this.options = { ...this.options, ...options };
  }

  /** Time of the latest frame, on the same clock as event times. */
  get nowMs(): number {
    return this.framesSeen * FRAME_MS;
  }

  get speaking(): boolean {
    return this.inSpeech;
  }

  /** A voice is being heard now: an utterance, or loud frames that may be one starting. */
  get hearingVoice(): boolean {
    return this.inSpeech || this.loudRun > 0;
  }

  /** Marks the current moment, e.g. when the app stops talking during a barge-in. */
  mark(): void {
    this.voicedSinceMark = 0;
  }

  /** Level of the latest frame above the noise floor in dB (for meters). */
  get levelDb(): number {
    return this.lastDb - this.noiseDb;
  }

  /** Current noise floor estimate in dBFS (for diagnostics and UI meters). */
  get noiseFloorDb(): number {
    return this.noiseDb;
  }

  push(chunk: Float32Array): EndpointerEvent[] {
    const merged = new Float32Array(this.pending.length + chunk.length);
    merged.set(this.pending);
    merged.set(chunk, this.pending.length);
    const events: EndpointerEvent[] = [];
    let offset = 0;
    for (; offset + this.frameSize <= merged.length; offset += this.frameSize) {
      events.push(...this.frame(merged.slice(offset, offset + this.frameSize)));
    }
    this.pending = merged.slice(offset);
    return events;
  }

  /** Ends any utterance in progress (for example when the microphone is switched off). */
  flush(): EndpointerEvent[] {
    return this.inSpeech ? this.finish(false) : [];
  }

  reset(): void {
    this.pending = new Float32Array(0);
    this.inSpeech = false;
    this.loudRun = this.quietRun = 0;
    this.preRoll = [];
    this.speech = [];
  }

  private frame(frame: Float32Array): EndpointerEvent[] {
    const o = this.options;
    this.framesSeen++;
    let energy = 0;
    for (const s of frame) energy += s * s;
    const db = 10 * Math.log10(energy / frame.length + 1e-12);
    this.lastDb = db;
    this.recentDb.push(db);
    if (this.recentDb.length > MIN_WINDOW_FRAMES) this.recentDb.shift();

    if (!this.calibrated) {
      // A voice well above the floor measured so far ends calibration early: the microphone may
      // open just as the child answers (it is muted while the app talks), and calibrating on the
      // word would raise the floor to speech level and hide it.
      const voice = this.framesSeen > 3 && db > this.noiseDb + o.onsetDb + CALIBRATION_VOICE_DB;
      if (this.framesSeen > CALIBRATION_FRAMES || voice) this.calibrated = true;
      else {
        // Calibration: follow the room level quickly in both directions; no speech detection yet.
        this.noiseDb = Math.max(o.minNoiseDb, this.noiseDb + 0.3 * (db - this.noiseDb));
        return [];
      }
    }
    if (!this.inSpeech) {
      // Track the floor: fall fast to quieter frames, rise slowly in steady noise.
      const rate = db < this.noiseDb ? 0.3 : 0.01;
      this.noiseDb = Math.max(o.minNoiseDb, this.noiseDb + rate * (db - this.noiseDb));
    } else if (this.recentDb.length === MIN_WINDOW_FRAMES) {
      // Speech never stays loud for 2 s without a dip; if even the quietest recent frame is well
      // above the floor, the "speech" is steady noise (a fan, a TV): let the floor rise to it.
      const quietest = Math.min(...this.recentDb);
      if (quietest > this.noiseDb + 3) this.noiseDb += 0.05 * (quietest - this.noiseDb);
    }

    const events: EndpointerEvent[] = [];
    if (!this.inSpeech) {
      this.preRoll.push(frame);
      const keep = Math.ceil(o.preRollMs / FRAME_MS) + o.onsetFrames;
      if (this.preRoll.length > keep) this.preRoll.shift();
      this.loudRun = db > this.onsetThreshold() ? this.loudRun + 1 : 0;
      if (o.echoGuard) this.learnEcho(db);
      if (this.loudRun >= o.onsetFrames) {
        this.inSpeech = true;
        this.quietRun = 0;
        this.speech = [...this.preRoll];
        this.preRoll = [];
        this.speechStartFrame = this.framesSeen - this.speech.length;
        this.voicedFrames = o.onsetFrames;
        // A mark before this utterance does not apply to it.
        this.voicedSinceMark = o.onsetFrames;
        this.peakDb = db;
        this.clipped = this.samples = 0;
        for (const f of this.speech) this.countClipped(f);
        events.push({
          type: 'speech-start',
          atMs: this.speechStartFrame * FRAME_MS,
          voiceAtMs: (this.framesSeen - this.loudRun) * FRAME_MS,
        });
      }
      return events;
    }

    this.speech.push(frame);
    this.countClipped(frame);
    this.peakDb = Math.max(this.peakDb, db);
    const aboveFloor = db >= this.noiseDb + o.offsetDb;
    const voiced = aboveFloor && db >= this.peakDb - o.peakDropDb;
    this.quietRun = voiced ? 0 : this.quietRun + 1;
    this.silentRun = aboveFloor ? 0 : this.silentRun + 1;
    if (voiced) {
      this.voicedFrames++;
      this.voicedSinceMark++;
    }
    const lengthMs = this.speech.length * FRAME_MS;
    if (this.quietRun * FRAME_MS >= o.hangoverMs) events.push(...this.finish(false));
    else if (lengthMs >= o.maxSpeechMs) events.push(...this.finish(true));
    return events;
  }

  /** Raises the learned echo level, e.g. after a "barge-in" turned out to be the app's own voice. */
  learnEchoLevel(db: number): void {
    this.echoMinDb = Math.max(this.echoMinDb, db - ECHO_MARGIN_DB);
    this.echoDb = Math.max(this.echoDb, this.echoMinDb);
  }

  /**
   * Called for frames outside speech while the app talks. Frames loud enough to start a barge-in
   * are not learned as echo (they may be the child).
   */
  private learnEcho(db: number) {
    this.echoFrames++;
    if (this.loudRun > 0) return;
    this.echoHistory.push(db);
    if (this.echoHistory.length > ECHO_HISTORY_FRAMES) this.echoHistory.shift();
    // Recomputed every 10 frames; the history is short, so sorting is cheap.
    if (this.echoFrames % 10 === 0 || this.echoDb === -Infinity) {
      const sorted = [...this.echoHistory].sort((a, b) => a - b);
      const p = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * ECHO_PERCENTILE))];
      this.echoDb = Math.max(p ?? -Infinity, this.echoMinDb);
    }
  }

  private onsetThreshold(): number {
    const o = this.options;
    let base = this.noiseDb + o.onsetDb;
    // Just after an utterance, its own soft ending (or quieter background) does not start a new one.
    if ((this.framesSeen - this.lastEndFrame) * FRAME_MS < REFRACTORY_MS)
      base = Math.max(base, this.lastPeakDb - o.peakDropDb);
    if (!o.echoGuard) return base;
    if (this.echoFrames < ECHO_CALIBRATION_FRAMES) return Infinity;
    return Math.max(base, this.echoDb + ECHO_MARGIN_DB);
  }

  private countClipped(frame: Float32Array) {
    for (const s of frame) if (Math.abs(s) >= CLIP_LEVEL) this.clipped++;
    this.samples += frame.length;
  }

  private finish(cut: boolean): EndpointerEvent[] {
    const o = this.options;
    // Trim most of the trailing silence (below the noise floor; a soft word ending stays) but
    // keep a little for the model's context.
    const tailKeep = Math.ceil(150 / FRAME_MS);
    const trailing = Math.min(this.quietRun, this.silentRun);
    const trimmed = this.speech.slice(
      0,
      Math.max(1, this.speech.length - Math.max(0, trailing - tailKeep)),
    );
    this.lastPeakDb = this.peakDb;
    this.lastEndFrame = this.framesSeen;
    const voicedMs = this.voicedFrames * FRAME_MS;
    this.inSpeech = false;
    this.speech = [];
    this.loudRun = this.quietRun = this.silentRun = 0;
    if (voicedMs < o.minSpeechMs)
      return [{ type: 'discarded', reason: 'too-short', peakDb: this.peakDb }];
    const audio = new Float32Array(trimmed.length * this.frameSize);
    trimmed.forEach((f, i) => {
      audio.set(f, i * this.frameSize);
    });
    const startMs = this.speechStartFrame * FRAME_MS;
    return [
      {
        type: 'utterance',
        audio,
        startMs,
        endMs: startMs + trimmed.length * FRAME_MS,
        cut,
        peakDb: this.peakDb,
        noiseDb: this.noiseDb,
        clippedShare: this.samples > 0 ? this.clipped / this.samples : 0,
        voicedMsSinceMark: this.voicedSinceMark * FRAME_MS,
      },
    ];
  }
}
